import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getApiBaseUrl } from '@/config/env';
import { formatCurrency } from '@/utils/format';

interface DisplayPayload {
  cartJson?: string;
  totalAmount: number;
  currencyCode?: string;
}

async function fetchDisplayPublic(token: string): Promise<DisplayPayload | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/pos/display/${encodeURIComponent(token)}`);
    if (!res.ok) return null;
    const json = (await res.json()) as { data?: DisplayPayload };
    return json.data ?? null;
  } catch {
    return null;
  }
}

export function PosCustomerDisplayPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<DisplayPayload | null>(null);

  useEffect(() => {
    if (!token) return;
    const poll = () => {
      void fetchDisplayPublic(token).then((payload) => {
        if (payload) setData(payload);
      });
    };
    poll();
    const id = window.setInterval(poll, 2000);
    return () => window.clearInterval(id);
  }, [token]);

  const currency = data?.currencyCode ?? 'INR';

  let items: { name?: string; quantity?: number; lineTotal?: number }[] = [];
  if (data?.cartJson) {
    try {
      const parsed = JSON.parse(data.cartJson) as { items?: typeof items };
      items = parsed.items ?? [];
    } catch {
      items = [];
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 text-white">
      <header className="border-b border-slate-800 px-8 py-4 text-center text-xl font-semibold">
        Your order
      </header>
      <main className="flex-1 px-8 py-6">
        <ul className="space-y-3">
          {items.map((item, i) => (
            <li key={i} className="flex justify-between text-lg">
              <span>{item.name ?? 'Item'} × {item.quantity ?? 1}</span>
              <span>{formatCurrency(item.lineTotal ?? 0, currency)}</span>
            </li>
          ))}
          {items.length === 0 && <li className="text-slate-500">Waiting for items…</li>}
        </ul>
      </main>
      <footer className="border-t border-slate-800 px-8 py-6 text-center">
        <p className="text-sm text-slate-400">Total</p>
        <p className="text-4xl font-bold">{formatCurrency(data?.totalAmount ?? 0, currency)}</p>
      </footer>
    </div>
  );
}
